import { DevicePageRespData } from "api/types/device";
import { useCallback, useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { accountSiteList } from "store/appSelectors";
import { getAlertsListFilters } from "store/page/pageSelectors";
import { AlertsPageFilters, setAlertsPageFilters } from "store/page/pageSlice";

import Combobox from "widgets/shared/inputs/combobox";
import { SearchInput } from "widgets/shared/inputs/search";
import Table from "widgets/controls/tables/genericTable";
import defineAlertsTablecolumns from "models/tables/alertsPage";
import { alertsPage } from "api/alerts";
import { AlertStateDropdown } from "models/dropdowns/alertsState";
import { t } from "i18next";
import SitesCombobox from "widgets/shared/inputs/usersCombobox";

const Alerts = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const columns = defineAlertsTablecolumns((info) => {
    const row = info.row.original;
    navigate(`/admin/alerts/details?id=${row.id}`);
  });
  const userInfo = useSelector(accountSiteList, shallowEqual);

  const [dataSource, setDataSource] = useState<DevicePageRespData[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Get initial filters from store once
  const initialFilters = useSelector(getAlertsListFilters);

  // Local state for filters - initialized once from Redux
  const [filters, setFilters] = useState<AlertsPageFilters>(
    () => initialFilters
  );

  const fetchData = useCallback(async () => {
    const { paginationState, sortingState, state, classes, snrs, search } =
      filters;
    setIsLoading(true);
    const { data, error, isLoading } = await alertsPage({
      search: search,
      pageIndex: paginationState.pageIndex + 1,
      pageSize: paginationState.pageSize,
      sort: sortingState[0].id,
      desc: sortingState[0].desc,
      state: state,
      classes: classes || userInfo.map((site) => site.id),
      snrs: snrs,
    });
    setIsLoading(isLoading);
    if (!error) {
      setDataSource(data.data || []);

      setFilters((prevFilters) => ({
        ...prevFilters,
        paginationState: {
          pageIndex: data.currentPage - 1,
          pageSize: paginationState.pageSize,
        },
        totCount: data.totalCount,
        pageCount: data.totalPages,
      }));

      dispatch(
        setAlertsPageFilters({
          paginationState: { pageIndex: 0, pageSize: 10 },
          sortingState: [{ desc: true, id: "created" }],
          totCount: 0,
          pageCount: 0,
          search: search,
          state: state,
          classes: classes,
          snrs: snrs,
        })
      );
    }
  }, [
    filters.paginationState.pageIndex,
    filters.paginationState.pageSize,
    filters.sortingState,
    filters.state,
    filters.classes,
    filters.snrs,
    filters.search,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div>
      <Table
        filterbar={
          <div className="flex h-full w-full flex-col gap-y-3">
            <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
              <div className="flex-1 lg:flex-grow-[1]">
                <SitesCombobox
                  filter
                  id="device-select-site"
                  placeholder={t("common.select.site")}
                  variant="solid"
                  color="secondary"
                  value={filters.classes}
                  onChange={(item) => {
                    setFilters((prevFilters) => ({
                      ...prevFilters,
                      classes: item,
                      paginationState: {
                        pageIndex: 0,
                        pageSize: prevFilters.paginationState.pageSize,
                      },
                    }));
                  }}
                  sizes="md"
                  rounded="rounded-xl"
                  disabled={isLoading}
                />
              </div>
              <div className="flex-1 lg:flex-grow-[1]">
                <Combobox
                  filter
                  id="device-select-state"
                  placeholder={t("common.select.state")}
                  variant="solid"
                  color="secondary"
                  value={filters.state}
                  onChange={(item) => {
                    setFilters((prevFilters) => ({
                      ...prevFilters,
                      state: item,
                      paginationState: {
                        pageIndex: 0,
                        pageSize: prevFilters.paginationState.pageSize,
                      },
                    }));
                  }}
                  items={AlertStateDropdown}
                  labelKey="label"
                  valueKey="value"
                  sizes="md"
                  rounded="rounded-xl"
                  disabled={isLoading}
                />
              </div>
              <div className="flex-1 lg:flex-grow-[1]">
                <SearchInput
                  onChange={(filters) => {
                    setFilters((prevFilters) => ({
                      ...prevFilters,
                      search: filters,
                      paginationState: {
                        pageIndex: 0,
                        pageSize: prevFilters.paginationState.pageSize,
                      },
                    }));
                  }}
                  type="text"
                  value={filters.search}
                  sizes="md"
                  rounded="rounded-xl"
                  color="secondary"
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>
        }
        loading={isLoading}
        data={dataSource}
        columns={columns}
        onRowClick={(row) => {
          navigate(`/admin/alerts/details?id=${row.id}`);
        }}
        pagination={filters.paginationState}
        paginationOptions={{
          onPaginationChange: (pagination) => {
            const page =
              typeof pagination === "function"
                ? pagination(filters.paginationState)
                : pagination;
            setFilters((prevFilters) => ({
              ...prevFilters,
              paginationState: page,
            }));
          },
          rowCount: filters.totCount,
          pageCount: filters.pageCount,
        }}
        sorting={filters.sortingState}
        onSortingChange={(updaterOrValue) => {
          const newSortingState =
            typeof updaterOrValue === "function"
              ? updaterOrValue(filters.sortingState)
              : updaterOrValue;
          if (newSortingState && newSortingState.length > 0) {
            setFilters((prevFilters) => ({
              ...prevFilters,
              sortingState: newSortingState,
            }));
          } else {
            setFilters((prevFilters) => ({
              ...prevFilters,
              sortingState: [{ id: "name", desc: false }],
            }));
          }
        }}
      />
    </div>
  );
};

export default Alerts;
